import ExcelJS from "exceljs";
import FileSaver from "file-saver";
import snakeCase from "lodash.snakecase";
import set from "lodash.set";

import { getProjectDataToXlsxMapper } from "../calculations/projectDataToXlsxMapper";
import {
  INTERSECTION_NUMBERS,
  STEP_STORAGE_KEYS,
  TRAFFIC_VOLUME_KEYS,
} from "../constants/storageConstants";
import {
  getCellMappingForScenario,
  getEmptyIntxVolumesObject,
  getPassengersPerVehicleCell,
  getScenarioSpreadsheetProps,
  getUnexpectedCellValueError,
} from "../constants/excelTemplateConstants";
import produce from "immer";
import {
  VOLUMES_UPLOAD_SUCCESS,
  handleTrafficVolumesUpload,
} from "../screens/trafficVolumes/trafficVolumesUploadHelper";

export const EXCEL_DOC_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * @param {string} [baseCaseId] - optional
 * @param {object} projectData
 * trigger the browser to download an xlsx file where the ICE form PennDOT-ICE-Forms.xlsx
 * has been filled out with project data from the web tool
 */
export const downloadPennDotIceFormXlsx = ({ baseCaseId, projectData }) => {
  const { projectName } = projectData[STEP_STORAGE_KEYS.PROJECT_INFORMATION];
  fetch(process.env.PUBLIC_URL + "/PennDOT-ICE-Forms.xlsx")
    .then((response) => response.arrayBuffer())
    .then((data) => {
      const workbook = new ExcelJS.Workbook();
      workbook.xlsx.load(data).then(() => {
        getProjectDataToXlsxMapper({ baseCaseId, projectData }).forEach(
          ({ worksheetName, cells }) => {
            const worksheet = workbook.getWorksheet(worksheetName);
            cells.forEach(({ value, cell }) => {
              worksheet.getCell(cell).value = value;
            });
          }
        );

        // Save the modified workbook
        workbook.xlsx.writeBuffer().then((buffer) => {
          const blob = new Blob([buffer], { type: EXCEL_DOC_TYPE });
          FileSaver.saveAs(
            blob,
            `${snakeCase(projectName ?? "PennDOT")}_ice_forms.xlsx`
          );
        });
      });
    })
    .catch((error) => {
      throw new Error("Error fetching the xlsx file:", error);
    });
};

/**
 * gets the href path to the excel file we use as a template for traffic volumes
 */
export const getExcelTemplateHref = ({
  isCapXFlow,
  isRampTerminalIntersection,
}) =>
  `PennDotWebIceDataUploadTemplate-${
    !isCapXFlow && isRampTerminalIntersection
      ? "rampTerminals"
      : "singleIntersection"
  }.xlsx`;

const isValidSpreadsheetValue = (value) =>
  value === null || (typeof value === "number" && value >= 0);

/**
 * @param {object} excelWorksheet exceljs worksheet object
 * @returns object with traffic volumes for one intersection. For example, ramp terminal intersections have 2 of these objects.
 * One called intersection1 and one called intersection2. CapX projects and at grade Life Cycle Cost projects just have one object.
 */
const getIntxTrafficVolumesFromExcel = ({
  excelWorksheet,
  intersectionNumber,
  isCapXFlow,
  isRampTerminalIntersection,
  isWeekendAnalysis,
  withPassengersPerVehicle,
}) => {
  return produce(getEmptyIntxVolumesObject(isWeekendAnalysis), (draft) => {
    // set volumes and truck percents
    getScenarioSpreadsheetProps({
      intersectionNumber,
      isCapXFlow,
      isRampTerminalIntersection,
      isWeekendAnalysis,
    }).forEach((data) => {
      getCellMappingForScenario({
        ...data,
        isRampTerminalIntersection,
      }).forEach(({ cell, multiplier = 1, path }) => {
        const value = excelWorksheet.getCell(cell).value;
        if (isValidSpreadsheetValue(value)) {
          draft = set(draft, path, value * multiplier);
        } else {
          throw getUnexpectedCellValueError({ cell, value });
        }
      });
    });

    // set passengers per vehicle
    if (withPassengersPerVehicle) {
      [
        TRAFFIC_VOLUME_KEYS.OPENING_YEAR,
        TRAFFIC_VOLUME_KEYS.DESIGN_YEAR,
      ].forEach((year) => {
        const cell = getPassengersPerVehicleCell({
          isOpeningYear: year === TRAFFIC_VOLUME_KEYS.OPENING_YEAR,
          isRampTerminalIntersection,
        });
        const value = excelWorksheet.getCell(cell).value;
        if (isValidSpreadsheetValue(value)) {
          draft[year].amPeak.passengersPerVehicle = value;
        } else {
          throw getUnexpectedCellValueError({ cell, value });
        }
      });
    }
  });
};

/**
 * Uploads a blank template for the user to fill with volumes, passengers per vehicle, truck percent
 */
export const onTrafficDataTemplateUpload = ({
  isCapXFlow,
  isRampTerminalIntersection,
  isWeekendAnalysis,
  onUpload,
  saveFormData,
  setUploadResult,
  templateFile,
}) => {
  if (templateFile) {
    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(templateFile);
    fileReader.onload = (event) => {
      const workbook = new ExcelJS.Workbook();
      workbook.xlsx.load(event.target.result).then(() => {
        const excelWorksheet = workbook.getWorksheet("UploadTemplate");
        const commonProps = {
          excelWorksheet,
          isCapXFlow,
          isRampTerminalIntersection,
          isWeekendAnalysis,
        };
        try {
          const trafficVolumes = isRampTerminalIntersection
            ? {
                [INTERSECTION_NUMBERS.INTERSECTION_1]:
                  getIntxTrafficVolumesFromExcel({
                    ...commonProps,
                    intersectionNumber: INTERSECTION_NUMBERS.INTERSECTION_1,
                    withPassengersPerVehicle: true,
                  }),
                [INTERSECTION_NUMBERS.INTERSECTION_2]:
                  getIntxTrafficVolumesFromExcel({
                    ...commonProps,
                    intersectionNumber: INTERSECTION_NUMBERS.INTERSECTION_2,
                    withPassengersPerVehicle: false,
                  }),
              }
            : getIntxTrafficVolumesFromExcel({
                ...commonProps,
                withPassengersPerVehicle: true,
              });
          handleTrafficVolumesUpload({
            trafficVolumes,
            isRampTerminalIntersection,
            isWeekendAnalysis,
            onUpload,
            setUploadResult,
            saveFormData,
          });
        } catch (error) {
          if (error.severity) {
            return setUploadResult(error);
          } else {
            throw error;
          }
        }
        setUploadResult(VOLUMES_UPLOAD_SUCCESS);
      });
    };
  }
};
